/* eslint-disable react/jsx-curly-brace-presence */
/**
 *
 * ModalLogin
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { showTermsOfUse, showPrivacyPolicy } from 'containers/Main/actions';
import { setAuthModalAction } from 'containers/Auth/actions';
import { makeSelectOAuthOptions } from 'containers/Main/selectors';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import FormControl from '@material-ui/core/FormControl';
import { LOGIN_CAPTION } from 'containers/Signup/constants';
import FacebookButton from 'components/SocialButtons/FacebookButton';
import GoogleButton from 'components/SocialButtons/GoogleButton';
import MicrosoftButton from 'components/SocialButtons/MicrosoftButton';
import Colors from 'utils/colors';

const styles = theme => ({
  container: {
    padding: '16px 32px',
    backgroundColor: 'white',
    width: 500,
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: '5%',
    left: '50%',
    marginLeft: -250,
    [theme.breakpoints.down('sm')]: {
      width: 300,
      left: '50%',
      marginLeft: -150,
    },
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerText: {
    color: '#003C29',
    fontSize: 24,
  },
  closeIcon: {
    padding: 6,
  },
  closeImage: {
    width: 24,
    height: 24,
  },
  caption: {
    marginTop: 10,
  },
  captionLink: {
    color: Colors.link,
    fontFamily: 'MadaMedium',
    cursor: 'pointer',
    '&:focus': {
      outlineWidth: 0,
    },
  },
  socialLoginContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  dividerText: {
    paddingLeft: 8,
    paddingRight: 8,
    width: '50%',
    textAlign: 'center',
    ...theme.typography.pxSmall,
    fontSize: '0.875rem',
  },
  formContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  labelCheck: {
    fontSize: 11,
    lineHeight: 'unset',
  },
  marginNone: {
    margin: 0,
    padding: 0,
  },
  checkboxApply: {
    padding: '0',
    paddingRight: 5,
  },
  textFieldContainer: {
    marginBottom: 16,
    '& label': {
      fontSize: '1.063rem',
    },
    '& input': {
      fontSize: '1.063rem',
    },
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    // marginTop: 16,
    marginBottom: 5,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
  },
  signUpText: {
    fontSize: 16,
  },
  signUpButton: {
    background: '#ffffff',
    color: theme.palette.primary.main,
    padding: 12,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      background: '#f6f6f6',
    },
  },
});

class ModalLogin extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      password: '',
      showPassword: false,
    };
  }

  handleCaptionClick = action => {
    const { doShowTermsOfUse, doShowPrivacyPolicy } = this.props;

    if (action === 'agreement') doShowTermsOfUse(true);
    if (action === 'privacy') doShowPrivacyPolicy(true);
  };

  handleSocialLogin = provider => {
    const { onSignUp } = this.props;
    onSignUp({
      provider: { provider, type: 'popup' },
    });
  };

  togglePasswordVisibility = () => {
    this.setState(prevState => ({
      showPassword: !prevState.showPassword,
    }));
  };

  handleSubmit = e => {
    e.preventDefault();

    const { onSignUp } = this.props;
    const { email, password } = this.state;

    onSignUp({ provider: { email, password } });
  };

  render() {
    const { email, password, showPassword } = this.state;
    const {
      classes,
      onClose,
      doSetSignUpModal,
      type,
      authOptions,
    } = this.props;

    return (
      <Grid container justify="center" className={classes.container}>
        <Grid item xs={12} className={classes.header}>
          <Typography className={classes.headerText}>
            Sign in to{' '}
            {type === 'topic'
              ? 'Favorite this Topic'
              : `${type.charAt(0).toUpperCase() + type.slice(1)} this Resource`}
          </Typography>
          <IconButton
            className={classes.closeIcon}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="caption"
            align="center"
            className={classes.caption}
          >
            {LOGIN_CAPTION.map(el => {
              if (el.action) {
                return (
                  <span
                    key={el.value}
                    role="link"
                    tabIndex={0}
                    onKeyPress={() => this.handleCaptionClick(el.action)}
                    className={classes.captionLink}
                    onClick={() => this.handleCaptionClick(el.action)}
                  >
                    {el.value}
                  </span>
                );
              }

              return el.value;
            })}
          </Typography>
        </Grid>
        {(authOptions.facebook || authOptions.google || authOptions.ms) && (
          <Grid item xs={12} className={classes.socialLoginContainer}>
            {authOptions.facebook && (
              <FacebookButton
                onClick={() => this.handleSocialLogin('facebook')}
              />
            )}
            {authOptions.google && (
              <GoogleButton onClick={() => this.handleSocialLogin('google')} />
            )}
            {authOptions.ms && (
              <MicrosoftButton
                onClick={() => this.handleSocialLogin('microsoft.com')}
              />
            )}
          </Grid>
        )}
        {(authOptions.facebook || authOptions.google || authOptions.ms) &&
          authOptions.cm && (
            <Grid item xs={12} className={classes.divider}>
              <div className={classes.dividerItem} />
              <Typography className={classes.dividerText}>
                or sign in with email
              </Typography>
              <div className={classes.dividerItem} />
            </Grid>
          )}
        {authOptions.cm && (
          <>
            <Grid item xs={12}>
              <form
                className={classes.formContainer}
                onSubmit={this.handleSubmit}
              >
                <TextField
                  name="email"
                  classes={{ root: classes.textFieldContainer }}
                  fullWidth
                  label="Email"
                  value={email}
                  onChange={e => this.setState({ email: e.target.value })}
                  type="email"
                  required
                />
                <FormControl
                  fullWidth
                  classes={{ root: classes.textFieldContainer }}
                  required
                >
                  <InputLabel htmlFor="adornment-password">Password</InputLabel>
                  <Input
                    id="adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => this.setState({ password: e.target.value })}
                    endAdornment={
                      <InputAdornment position="end">
                        <FormControlLabel
                          classes={{
                            label: classes.labelCheck,
                            root: classes.marginNone,
                          }}
                          control={
                            <Checkbox
                              disableRipple
                              classes={{ root: classes.checkboxApply }}
                              checkedIcon={
                                <CheckBoxIcon
                                  color="primary"
                                  style={{ fontSize: 14 }}
                                />
                              }
                              inputProps={{ 'aria-label': 'Show Password' }}
                              icon={
                                <CheckBoxOutlineBlankIcon
                                  style={{ fontSize: 14 }}
                                />
                              }
                              checked={showPassword}
                              onChange={() =>
                                this.togglePasswordVisibility('showPassword')
                              }
                            />
                          }
                          label={showPassword ? 'Hide' : 'Show'}
                        />
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <ButtonBase className={classes.button} type="submit">
                  SIGN IN
                </ButtonBase>
              </form>
            </Grid>
            <Grid item xs={12} className={classes.divider}>
              <div className={classes.dividerItem} />
            </Grid>
            <Grid item xs={12} className={classes.footer}>
              <Typography className={classes.signUpText}>
                {`Don't have an account?`}
              </Typography>
              <ButtonBase
                className={classes.signUpButton}
                onClick={doSetSignUpModal}
              >
                Sign Up
              </ButtonBase>
            </Grid>
          </>
        )}
      </Grid>
    );
  }
}

ModalLogin.propTypes = {
  authOptions: PropTypes.object,
  classes: PropTypes.object,
  onClose: PropTypes.func,
  doSetSignUpModal: PropTypes.func,
  doShowTermsOfUse: PropTypes.func,
  doShowPrivacyPolicy: PropTypes.func,
  onSignUp: PropTypes.func,
  type: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  authOptions: makeSelectOAuthOptions(),
});

function mapDispatchToProps(dispatch) {
  return {
    doSetSignUpModal: () => dispatch(setAuthModalAction('sign-up')),
    doShowTermsOfUse: payload => dispatch(showTermsOfUse(payload)),
    doShowPrivacyPolicy: payload => dispatch(showPrivacyPolicy(payload)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  withStyles(styles),
)(ModalLogin);
